import classNames from 'classnames';
import { FC, useRef, Fragment, useContext } from 'react';
import { useStickyTable } from 'react-sticky-table-hook';
import { LoadingTable } from '../../Core/Components/LoadingTable/LoadingTable';
import { useScrolled } from '../../Core/Hooks/useScrolled';
import {
	ProductEntry,
	Location,
	StoreOrder,
	DeliveryType,
	LineItem,
} from '../../Core/SharedTypes/Models';
import { Pill } from '../../Core/Components/Pill/Pill';
import { NullScreen } from '../../Core/Components/NullScreen/NullScreen';

import './StoreOrderBookTableExpanded.scss';
import { Icon } from '../../Core/Components/Icon/Icon';
import { format } from 'date-fns';
import { Pages } from '../../Core/Enums';
import { generateTitle } from '../../Core/Utils/GenerateTitles';
import { DateContext } from '../../Core/Contexts/DateContext';

export type Props = {
	currentLocation: Location | undefined;
	orders: StoreOrder[] | undefined;
	isLoading: boolean;
	handleOrderClick: (order: StoreOrder) => void;
};

export const StoreOrderBookTableExpanded: FC<Props> = ({
	currentLocation,
	orders,
	isLoading,
	handleOrderClick,
}) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [isScrolled] = useScrolled(scrollRef);

	const page = Pages.ORDERBOOK_STORE_PAGE;
	const [selectedDate] = useContext(DateContext);
	const printTitle = generateTitle(selectedDate[0], page);

	const tableConfig = useStickyTable({
		columns: [
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 100vw)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 250px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
		],
	});

	const tableConfigLineItems = useStickyTable({
		columns: [
			{
				width: 'minmax(auto, 100vw)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 250px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
			{
				width: 'minmax(auto, 120px)',
				isSticky: false,
			},
		],
	});

	return (
		<div
			className="table table__scrollable"
			style={{ ...tableConfig.table.style }}
			ref={scrollRef}
		>
			{printTitle && (
				<div className="print-only-footer">
					<h1 className="print-only-footer__date">{printTitle}</h1>
					<h1 className="print-only-footer__location">
						{currentLocation?.title}
					</h1>
				</div>
			)}

			{/* LOADING */}
			{isLoading && <LoadingTable />}

			{/* HEADER */}
			{orders && (
				<div
					style={{
						...tableConfig.row.sticky.style,
						gap: '1rem',
					}}
					className="store-order-table-row store-order-table-row--header"
				>
					{[
						'Afhentningstid',
						'Ordrenummer',
						'Kunde',
						'Status',
						'Betaling',
						'',
					].map((header, index) => (
						<div
							key={header}
							style={{
								...tableConfig.cells.sticky[index].style,
								minWidth: index === 2 ? '200px' : '120px',
							}}
							className={classNames(['table__header__cell'], {
								'table__header__cell--shadowed': isScrolled && index === 0,
							})}
						>
							<p
								className={classNames(['table__header__cell__data'], {
									'table__header__cell__data--title': index === 0,
									'store-order-table-row__header-row': index !== 0,
									'store-order-table-row__header-row--last': index === 5,
								})}
							>
								{header}
							</p>
						</div>
					))}
				</div>
			)}
			{/* TABLE CONTENT */}
			{orders && orders.length > 0 && !isLoading
				? orders.map((order: StoreOrder) => {
						return (
							<div key={order.id} className="store-order-print-container">
								<div
									style={{
										...tableConfig.row.scrolling.style,
										gap: '1rem',
									}}
									className="store-order-table-row"
									onClick={() => {
										handleOrderClick(order);
									}}
								>
									<div
										style={{
											...tableConfig.cells.scrolling[0].style,
											minWidth: '120px',
										}}
										className={classNames(
											['table__cell', 'table__cell--title'],
											{
												'table__cell--shadowed': isScrolled,
											},
										)}
									>
										{format(Date.parse(order.pickupTime), 'HH:mm')}
									</div>
									<div
										style={{
											...tableConfig.cells.scrolling[1].style,
											minWidth: '120px',
										}}
										className="table__cell store-order-table-row__data"
									>
										{order.orderNumber}
									</div>
									<div
										style={{
											...tableConfig.cells.scrolling[2].style,
											minWidth: '120px',
										}}
										className="table__cell store-order-table-row__data"
									>
										{order.customerName}
									</div>
									<div
										style={{
											...tableConfig.cells.scrolling[3].style,
											minWidth: '120px',
										}}
										className="table__cell  store-order-table-row__data"
									>
										<Pill text={order.statusText} color={order.statusColor} />
										{!order.dateCancelled &&
										order.deliveryType == 'delivery' ? (
											<div className="pill">
												<Pill text="Leveres" color="dark-grey" />
											</div>
										) : null}
									</div>
									<div
										style={{
											...tableConfig.cells.scrolling[4].style,
											minWidth: '120px',
										}}
										className="table__cell store-order-table-row__data"
									>
										<Pill
											text={order.paidStatusText}
											color={order.paidStatusColor}
										/>
									</div>

									<div
										style={{
											...tableConfig.cells.scrolling[4].style,
											marginLeft: '0px',
											minWidth: '120px',
										}}
										className="table__cell store-order-table-row__data store-order-table-row__data--action"
									>
										<Icon width={36} name="Plus" />
									</div>
								</div>
								<div className="line-item-table">
									{order &&
									order.lineItems &&
									order.lineItems.length > 0 &&
									!isLoading
										? order.lineItems.map((lineItem: LineItem) => {
												return (
													<div
														key={lineItem.id}
														style={{
															...tableConfigLineItems.row.scrolling.style,
															gap: '1rem',
														}}
														className="store-order-table-row store-order-table-row--line-item"
													>
														<div
															style={{
																...tableConfig.cells.scrolling[0].style,
																minWidth: '120px',
															}}
															className="table__cell store-order-table-row__data"
														>
															{lineItem.description}
														</div>
														<div
															style={{
																...tableConfig.cells.scrolling[1].style,
																minWidth: '120px',
															}}
															className="table__cell store-order-table-row__data"
														></div>
														<div
															style={{
																...tableConfig.cells.scrolling[1].style,
																minWidth: '120px',
															}}
															className="table__cell store-order-table-row__data"
														></div>
														<div
															style={{
																...tableConfig.cells.scrolling[1].style,
																minWidth: '90px',
															}}
															className="table__cell store-order-table-row__data"
														>
															{lineItem.planning == 1 ? (
																<Pill
																	text="Kommer fra centralen"
																	color="transparent"
																/>
															) : (
																''
															)}
														</div>
														<div
															style={{
																...tableConfig.cells.scrolling[2].style,
																minWidth: '120px',
															}}
															className="table__cell store-order-table-row__data"
														>
															{lineItem.qty}
														</div>
													</div>
												);
											})
										: null}
								</div>
							</div>
						);
					})
				: !isLoading && <NullScreen text="Ingen ordrer oprettet på dagen" />}
		</div>
	);
};
